import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  draggable,
  dropTargetForElements,
  monitorForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { disableNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/disable-native-drag-preview';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { Box } from '@/UI/Box/Box';
import { Stack } from '@/UI/Stack/Stack';
import { color } from '@/colors';
import { themeColor } from '@/UI/themeColors';
import { FlexCenVer } from '@/UI/styles';
import { AppXBtn } from '@/UI/AppXBtn/AppXBtn';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { ColorPickerField } from '@/UI/ColorPicker/ColorPickerField';
import { DragHandleIcon } from '@/UI/Icons/DragHandleIcon';
import { useIsHovered } from '@/hooks/useIsHovered';
import { I18N_KEYS } from '@/language/keys';
import { formatHexCode } from '@/UI/ColorPicker/colorPicker.utils';
import type { Color as WasmColor } from 'web';
import type { WebColor } from '@/UI/ColorPicker/ColorPicker';

const MIN_COLORS = 1;
const MAX_COLORS = 100;
const WHITE: WasmColor = { r: 255, g: 255, b: 255, a: 1.0 };

// Distinct from the canvas Color palette node's DnD type so the two pickers don't see each other.
const DESIGN_APP_COLOR_PALETTE_DND_TYPE = 'design-app-color-palette-row';

interface DesignAppColorPaletteInputProps {
  id: string;
  value: { colors: WasmColor[] } | undefined;
  onChange: (id: string, value: { colors: WasmColor[] }) => void;
  disabled?: boolean;
}

interface RowProps {
  inputId: string;
  index: number;
  color: WasmColor;
  rowKey: string;
  canRemove: boolean;
  disabled: boolean;
  onColorChange: (index: number, next: WebColor) => void;
  onRemove: (index: number) => void;
}

function ColorRow({ inputId, index, color: c, canRemove, disabled, onColorChange, onRemove }: RowProps) {
  const { t } = useTranslation();
  const rootRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLDivElement>(null);
  const [dropEdge, setDropEdge] = useState<'top' | 'bottom'>();
  const [isDragging, setIsDragging] = useState(false);
  const { isHovered, onMouseEnter, onMouseLeave } = useIsHovered();

  useEffect(() => {
    const el = rootRef.current;
    const handle = handleRef.current;
    if (!el || !handle) return;

    const cleanupDraggable = draggable({
      element: el,
      dragHandle: handle,
      getInitialData: () => ({ type: DESIGN_APP_COLOR_PALETTE_DND_TYPE, inputId, index }),
      // Don't render the translucent drag-image clone; the source row stays put
      // and is highlighted in place, with only the drop indicator moving.
      onGenerateDragPreview: ({ nativeSetDragImage }) => {
        disableNativeDragPreview({ nativeSetDragImage });
      },
      onDragStart: () => setIsDragging(true),
      onDrop: () => setIsDragging(false),
    });

    const cleanupDropTarget = dropTargetForElements({
      element: el,
      getData: () => ({ type: DESIGN_APP_COLOR_PALETTE_DND_TYPE, inputId, index }),
      canDrop: ({ source }) =>
        source.data.type === DESIGN_APP_COLOR_PALETTE_DND_TYPE &&
        source.data.inputId === inputId &&
        source.data.index !== index,
      onDragEnter: ({ source }) => {
        setDropEdge(Number(source.data.index) < index ? 'bottom' : 'top');
      },
      onDragLeave: () => setDropEdge(undefined),
      onDrop: () => setDropEdge(undefined),
    });

    return () => {
      cleanupDraggable();
      cleanupDropTarget();
    };
  }, [inputId, index]);

  return (
    <FlexCenVer
      ref={rootRef}
      // Marker so the outer design-app input-group's drag handlers can identify
      // events originating from a color row and skip them. We can't stop event
      // propagation here because pragmatic-dnd listens on `document` and needs
      // the native event to bubble.
      data-design-app-color-palette-row="true"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      sx={{
        position: 'relative',
        // Own stacking context so the dragging highlight's `zIndex: -1` layer
        // stays behind the row's content instead of escaping behind the panel.
        isolation: 'isolate',
        width: '100%',
        gap: 1,
        cursor: 'default',
        '& .design-app-color-palette-row-drag-handle': {
          position: 'absolute',
          left: -16,
          top: 0,
          width: 16,
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: token.text,
          opacity: 0,
          cursor: 'grab',
        },
        '&:hover .design-app-color-palette-row-drag-handle': {
          opacity: 1,
        },
        '& .design-app-color-palette-row-drag-handle:active': {
          cursor: 'grabbing',
        },
      }}
    >
      {isDragging && (
        <Box
          sx={{
            // Highlight the row being dragged in place (spans the drag-handle
            // gutter on the left). Sits behind the row content.
            position: 'absolute',
            left: -16,
            right: -16,
            top: 0,
            bottom: 0,
            background: themeColor.white16_T,
            zIndex: -1,
            pointerEvents: 'none',
          }}
        />
      )}

      {dropEdge && (
        <Box
          sx={{
            // 1px white drop indicator spanning the full row width, from the
            // drag-handle gutter across.
            position: 'absolute',
            left: -16,
            right: -16,
            [dropEdge === 'top' ? 'top' : 'bottom']: -4,
            height: '1px',
            background: color.White100,
            zIndex: 1,
            pointerEvents: 'none',
          }}
        />
      )}

      <Box
        ref={handleRef}
        className="design-app-color-palette-row-drag-handle"
        aria-label={t(I18N_KEYS.RECIPE_MAIN.NODES.COLOR_PALETTE.REORDER_ARIA)}
      >
        <DragHandleIcon aria-hidden />
      </Box>

      <ColorPickerField
        color={c}
        onChange={(next) => onColorChange(index, next)}
        onChangeEnd={(next) => onColorChange(index, next)}
        showAlpha={false}
        disabled={disabled}
      />

      <AppXBtn
        onClick={() => onRemove(index)}
        size={20}
        disabled={disabled || !canRemove}
        aria-label={t(I18N_KEYS.RECIPE_MAIN.NODES.COLOR_PALETTE.REMOVE_ARIA, { hex: formatHexCode(c) })}
        sx={{
          marginLeft: 'auto',
          visibility: isHovered && canRemove ? 'visible' : 'hidden',
          transition: 'visibility 0s, background-color 0.2s ease, color 0.2s ease',
        }}
      />
    </FlexCenVer>
  );
}

export default function DesignAppColorPaletteInput({
  id,
  value,
  onChange,
  disabled = false,
}: DesignAppColorPaletteInputProps) {
  const { t } = useTranslation();
  const colors = useMemo(() => (value?.colors?.length ? value.colors : [WHITE]), [value?.colors]);

  // Stable per-row keys so per-row UI state follows reorders rather than being keyed by index.
  // Mirrors `ColorPaletteView.getRowKeys()` for the canvas Color palette node.
  const rowKeysRef = useRef<string[]>([]);
  const nextKeyIdRef = useRef(0);
  const rowKeys = useMemo(() => {
    const next = rowKeysRef.current.slice(0, colors.length);
    while (next.length < colors.length) {
      nextKeyIdRef.current += 1;
      next.push(`design-app-color-palette-row-${nextKeyIdRef.current}`);
    }
    rowKeysRef.current = next;
    return next;
  }, [colors]);

  const commit = useCallback((next: WasmColor[]) => onChange(id, { colors: next }), [id, onChange]);

  const handleColorChange = useCallback(
    (index: number, next: WebColor) => {
      const updated = colors.slice();
      updated[index] = next;
      commit(updated);
    },
    [colors, commit],
  );

  const handleRemove = useCallback(
    (index: number) => {
      if (colors.length <= MIN_COLORS) return;
      rowKeysRef.current.splice(index, 1);
      const updated = colors.slice();
      updated.splice(index, 1);
      commit(updated);
    },
    [colors, commit],
  );

  const handleAdd = useCallback(() => {
    if (colors.length >= MAX_COLORS) return;
    commit([...colors, WHITE]);
  }, [colors, commit]);

  const handleReorder = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (
        fromIndex === toIndex ||
        fromIndex < 0 ||
        toIndex < 0 ||
        fromIndex >= colors.length ||
        toIndex >= colors.length
      ) {
        return;
      }
      const nextKeys = rowKeysRef.current.slice();
      const [movedKey] = nextKeys.splice(fromIndex, 1);
      nextKeys.splice(toIndex, 0, movedKey);
      rowKeysRef.current = nextKeys;
      const next = colors.slice();
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      commit(next);
    },
    [colors, commit],
  );

  useEffect(() => {
    return monitorForElements({
      canMonitor: ({ source }) => source.data.type === DESIGN_APP_COLOR_PALETTE_DND_TYPE && source.data.inputId === id,
      onDrop({ source, location }) {
        const target = location.current.dropTargets[0];
        if (!target) return;
        const fromIndex = source.data.index;
        const toIndex = target.data.index;
        if (typeof fromIndex !== 'number' || typeof toIndex !== 'number') return;
        handleReorder(fromIndex, toIndex);
      },
    });
  }, [id, handleReorder]);

  const canRemove = colors.length > MIN_COLORS;

  return (
    <Box sx={{ width: '100%', mt: 1 }}>
      <Stack spacing={1}>
        {colors.map((c, i) => (
          <ColorRow
            key={rowKeys[i]}
            inputId={id}
            index={i}
            color={c}
            rowKey={rowKeys[i]}
            canRemove={canRemove}
            disabled={disabled}
            onColorChange={handleColorChange}
            onRemove={handleRemove}
          />
        ))}
        <Box>
          <ButtonContained
            mode="text"
            size="small"
            disabled={disabled || colors.length >= MAX_COLORS}
            onClick={handleAdd}
          >
            {t(I18N_KEYS.RECIPE_MAIN.NODES.COLOR_PALETTE.ADD_ANOTHER)}
          </ButtonContained>
        </Box>
      </Stack>
    </Box>
  );
}
